import React, { useState, useRef, useEffect, useCallback } from 'react';
import { VolumeX } from 'lucide-react';
import {
  initAudio,
  setAmbientMusicVolumeRatio,
  getSoundFXEnabled,
  setSoundFXEnabled,
  subscribeSoundFX,
} from '../utils/audio';
import { getOptimizedImageUrl } from '../lib/utils';

const HERO_BASE_VOLUME = 0.05; // Calibrated to 25% volume level

const DEFAULT_HERO = {
  hero_overskrift: 'KALAS',
  hero_underoverskrift: 'Destilleriet Als · Sydals',
  hero_beskrivelse: 'Eksklusivt kystdestilleri med håndplukkede botanicals, langsom kobberdestillation og kompromisløs kvalitet.',
  hero_video_url: 'https://assets.auxodev.dk/assets/c8fc1da7-d576-4041-8666-592c544bae24/engelsk-voiceover.mp4',
  hero_billede: 'https://assets.auxodev.dk/assets/c8fc1da7-d576-4041-8666-592c544bae24/img-7021-650x867.jpg',
  hero_knap_tekst: 'Udforsk Universet',
  hero_knap_2_tekst: 'Besøg Destilleriet',
};

export default function Hero({ onScrollDown }) {
  const [heroData, setHeroData] = useState(DEFAULT_HERO);

  useEffect(() => {
    async function fetchHeroData() {
      try {
        const res = await fetch('/_api/public/v2/content/forside');
        if (res.ok) {
          const json = await res.json();
          if (json?.items?.[0]) {
            setHeroData((prev) => ({
              ...prev,
              ...json.items[0],
            }));
          }
        }
      } catch (err) {
        console.warn('Using fallback hero data:', err);
      }
    }
    fetchHeroData();
  }, []);

  const heroVideo = heroData.hero_video_url || DEFAULT_HERO.hero_video_url;

  const videoRef = useRef(null);
  const userMutedRef = useRef(false);
  const [isVideoMuted, setIsVideoMuted] = useState(!getSoundFXEnabled());
  const [isDarkOverlay, setIsDarkOverlay] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);
  const [isTitleVisible, setIsTitleVisible] = useState(false);

  // Smooth scroll crossfade calculation
  const updateAudioCrossfade = useCallback(() => {
    if (!videoRef.current) return;

    const heroEl = document.getElementById('hero');
    const heroHeight = heroEl ? heroEl.offsetHeight : window.innerHeight;
    const scrollY = window.scrollY || window.pageYOffset || 0;

    // Transition window: starts at 5% scroll, smoothly crossfades until 75% of hero height
    const fadeStart = heroHeight * 0.05;
    const fadeEnd = heroHeight * 0.75;
    let progress = 0;

    if (scrollY <= fadeStart) {
      progress = 0;
    } else if (scrollY >= fadeEnd) {
      progress = 1;
    } else {
      // Smooth cosine interpolation (S-curve) for ultra-natural acoustic crossfade
      const linear = (scrollY - fadeStart) / (fadeEnd - fadeStart);
      progress = 0.5 - 0.5 * Math.cos(Math.PI * linear);
    }

    const globalSoundEnabled = getSoundFXEnabled();
    const heroVolumeRatio = 1 - progress;
    const ambientRatio = progress;

    // 1. Update Video Audio
    if (globalSoundEnabled && !userMutedRef.current) {
      const targetVideoVol = Math.max(0, Math.min(1, HERO_BASE_VOLUME * heroVolumeRatio));
      videoRef.current.volume = targetVideoVol;
      if (targetVideoVol <= 0.005) {
        videoRef.current.muted = true;
      } else {
        videoRef.current.muted = false;
      }
    } else {
      videoRef.current.volume = 0;
      videoRef.current.muted = true;
    }

    // 2. Crossfade Web Audio Ambient Sound in parallel
    setAmbientMusicVolumeRatio(globalSoundEnabled ? ambientRatio : 0, 0.15);

    // 3. Pause video when far down the page to conserve CPU/battery, resume when scrolling up
    if (scrollY > heroHeight * 1.6) {
      if (!videoRef.current.paused) {
        videoRef.current.pause();
      }
    } else {
      if (videoRef.current.paused && !videoEnded) {
        videoRef.current.play().catch(() => {});
      }
    }
  }, [videoEnded]);

  // Initialize video & sync with global sound preferences
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const globalSoundEnabled = getSoundFXEnabled();
    video.volume = globalSoundEnabled ? HERO_BASE_VOLUME : 0;
    video.muted = !globalSoundEnabled;

    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          if (globalSoundEnabled) {
            setIsVideoMuted(false);
            userMutedRef.current = false;
          }
        })
        .catch(() => {
          // Browser autoplay fallback (mute until first user interaction)
          video.muted = true;
          setIsVideoMuted(true);
          video.play().catch(() => {});
        });
    }

    // Initialize crossfade state for current scroll position
    updateAudioCrossfade();

    // Listen to scroll with animation frame throttle
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          updateAudioCrossfade();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });

    // Handle first user gesture to resume audio context if suspended
    const handleFirstGesture = () => {
      if (getSoundFXEnabled()) {
        initAudio();
      }
    };

    window.addEventListener('pointerdown', handleFirstGesture, { once: true });
    window.addEventListener('keydown', handleFirstGesture, { once: true });

    // Subscribe to global sound toggle changes
    const unsubscribe = subscribeSoundFX((active) => {
      setIsVideoMuted(!active);
      userMutedRef.current = !active;
      if (!active) {
        if (videoRef.current) {
          videoRef.current.muted = true;
          videoRef.current.volume = 0;
        }
      } else {
        if (videoRef.current) {
          videoRef.current.muted = false;
          videoRef.current.volume = HERO_BASE_VOLUME;
          videoRef.current.play().catch(() => {});
        }
        updateAudioCrossfade();
      }
    });

    // Fallback timer to ensure title & text reveal smoothly even if video playback event is delayed
    const titleFallbackTimer = setTimeout(() => {
      setIsTitleVisible(true);
    }, 1400);

    return () => {
      clearTimeout(titleFallbackTimer);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      window.removeEventListener('pointerdown', handleFirstGesture);
      window.removeEventListener('keydown', handleFirstGesture);
      unsubscribe();
    };
  }, [updateAudioCrossfade]);

  // Track playback to trigger title reveal ~1s after video starts playing
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      if (videoRef.current.currentTime >= 1.2 && !isTitleVisible) {
        setIsTitleVisible(true);
      }
      if (videoRef.current.duration) {
        const remaining = videoRef.current.duration - videoRef.current.currentTime;
        if (remaining <= 1.2 && !isDarkOverlay) {
          setIsDarkOverlay(true);
        }
      }
    }
  };

  const handleVideoEnded = () => {
    setIsDarkOverlay(true);
    setIsTitleVisible(true);
    setVideoEnded(true);
  };

  const toggleVideoSound = (e) => {
    if (e) {
      e.stopPropagation();
    }
    const currentEnabled = getSoundFXEnabled();
    const nextEnabled = !currentEnabled;
    setSoundFXEnabled(nextEnabled);
    setIsVideoMuted(!nextEnabled);
    userMutedRef.current = !nextEnabled;

    if (!nextEnabled) {
      if (videoRef.current) {
        videoRef.current.muted = true;
        videoRef.current.volume = 0;
      }
    } else {
      initAudio();
      if (videoRef.current) {
        videoRef.current.muted = false;
        videoRef.current.volume = HERO_BASE_VOLUME;
        videoRef.current.play().catch(() => {});
      }
      updateAudioCrossfade();
    }
  };

  return (
    <section
      id="hero"
      className="relative min-h-[100dvh] w-full flex flex-col justify-between items-center text-center px-4 sm:px-6 pt-24 sm:pt-28 pb-10 overflow-hidden bg-[#1E1B18]"
      aria-label="Hero - KALAS Distillery"
    >
      {/* Background Visual Layer: Cinematic video with warm natural coastal grading */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none bg-[#1E1B18]">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          loop
          muted={isVideoMuted}
          preload="auto"
          poster={getOptimizedImageUrl(heroData.hero_billede || DEFAULT_HERO.hero_billede, 1600, 80)}
          onTimeUpdate={handleTimeUpdate}
          onEnded={handleVideoEnded}
          onError={handleVideoEnded}
          className="w-full h-full object-cover object-center brightness-[0.78] contrast-[1.02] scale-[1.02]"
        >
          <source src={heroVideo} type="video/mp4" />
        </video>

        {/* Clean, uniform semi-transparent dark veil without harsh local bands */}
        <div className="absolute inset-0 bg-[#1E1B18]/50 pointer-events-none" />
      </div>

      {/* Top spacer */}
      <div className="relative z-10 w-full h-4 sm:h-8" aria-hidden="true" />

      {/* Main Core Graphic Typography: Scandinavian Light Statement */}
      <div className="relative z-10 max-w-4xl mx-auto my-auto flex flex-col items-center justify-center px-4 select-none text-center">
        <span
          className={`font-['Montserrat'] text-[11px] sm:text-xs uppercase tracking-[0.35em] text-[#F6F2EB]/85 font-light mb-4 sm:mb-6 block drop-shadow-sm transition-all duration-700 ease-out transform ${
            isTitleVisible
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-3'
          }`}
        >
          {heroData.hero_underoverskrift || DEFAULT_HERO.hero_underoverskrift}
        </span>
        <h1
          className="font-['Cormorant_Garamond'] font-light text-5xl sm:text-7xl md:text-8xl lg:text-9xl tracking-[0.2em] sm:tracking-[0.28em] text-[#F6F2EB] uppercase leading-[1.02] drop-shadow-md whitespace-nowrap"
        >
          <span
            className={`inline-block transition-all duration-1000 ease-out transform ${
              isTitleVisible
                ? 'opacity-100 translate-y-0 filter-none scale-100'
                : 'opacity-0 translate-y-6 blur-xs scale-95'
            }`}
          >
            {heroData.hero_overskrift || DEFAULT_HERO.hero_overskrift}
          </span>
        </h1>
        <p
          className={`mt-5 sm:mt-7 max-w-xl mx-auto font-['Montserrat'] text-xs sm:text-sm text-[#F6F2EB]/90 font-light tracking-wide leading-relaxed drop-shadow-sm transition-all duration-1000 ease-out delay-500 transform ${
            isTitleVisible
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-4'
          }`}
        >
          {heroData.hero_beskrivelse || DEFAULT_HERO.hero_beskrivelse}
        </p>

        {/* Action Buttons */}
        <div
          className={`mt-8 sm:mt-10 flex flex-wrap items-center justify-center gap-3 sm:gap-4 transition-all duration-1000 ease-out delay-700 transform ${
            isTitleVisible
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-4 pointer-events-none'
          }`}
        >
          <button
            type="button"
            onClick={() => onScrollDown?.('intro')}
            className="px-6 py-3 border border-[#F6F2EB]/60 hover:border-[#F6F2EB] bg-[#37332F]/30 hover:bg-[#F6F2EB] text-[#F6F2EB] hover:text-[#37332F] font-['Montserrat'] text-xs uppercase tracking-[0.25em] font-medium transition-all duration-300 backdrop-blur-xs cursor-pointer"
          >
            {heroData.hero_knap_tekst || DEFAULT_HERO.hero_knap_tekst}
          </button>
          <button
            type="button"
            onClick={() => onScrollDown?.('contact')}
            className="px-6 py-3 border border-[#C99850]/90 hover:border-[#C99850] bg-[#C99850]/30 hover:bg-[#C99850] text-[#F6F2EB] hover:text-[#37332F] font-['Montserrat'] text-xs uppercase tracking-[0.25em] font-medium transition-all duration-300 backdrop-blur-xs cursor-pointer"
          >
            {heroData.hero_knap_2_tekst || DEFAULT_HERO.hero_knap_2_tekst}
          </button>
        </div>
      </div>

      {/* Bottom Area: Minimal Scroll Action & Sound Toggle */}
      <div className="relative z-20 w-full flex items-center justify-between sm:justify-end px-4 sm:px-6 min-h-[44px]">
        {/* Center: Scroll down indicator */}
        <div className="absolute left-1/2 -translate-x-1/2 bottom-0 flex flex-col items-center pointer-events-auto">
          <button
            type="button"
            onClick={() => onScrollDown?.('intro')}
            className="group flex flex-col items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#F6F2EB]/75 hover:text-[#F6F2EB] cursor-pointer focus:outline-none transition-colors"
            aria-label="Scroll til introduktion"
          >
            <span className="text-[10px] sm:text-[11px] tracking-[0.25em] font-normal text-[#F6F2EB]/90">Scroll</span>
            <div className="w-px h-7 bg-gradient-to-b from-[#F6F2EB]/70 to-transparent group-hover:h-10 transition-all duration-300" />
          </button>
        </div>

        {/* Right: Sound Toggle for Video */}
        <div className="shrink-0 relative z-10 ml-auto">
          <button
            type="button"
            onClick={toggleVideoSound}
            aria-label={isVideoMuted ? 'Slå lyd til' : 'Slå lyd fra'}
            aria-pressed={!isVideoMuted}
            className={`group inline-flex items-center gap-2.5 px-3.5 sm:px-4 py-2 border backdrop-blur-md text-[11px] sm:text-xs uppercase tracking-[0.2em] font-normal transition-all duration-300 cursor-pointer focus:outline-none ${
              isVideoMuted
                ? 'border-[#37332F]/30 bg-[#F6F2EB]/90 hover:bg-[#F6F2EB] text-[#37332F]'
                : 'border-[#C99850] bg-[#F6F2EB] text-[#37332F]'
            }`}
          >
            {isVideoMuted ? (
              <>
                <VolumeX
                  className="w-3.5 h-3.5 text-[#37332F]/70 group-hover:text-[#37332F] transition-colors"
                  aria-hidden="true"
                />
                <span className="font-normal tracking-[0.18em]">Lyd fra</span>
              </>
            ) : (
              <>
                <div className="flex items-center gap-0.5 h-3.5" aria-hidden="true">
                  <span className="w-0.5 h-3 bg-[#C99850] animate-[pulse_1s_ease-in-out_infinite]" />
                  <span className="w-0.5 h-4 bg-[#37332F] animate-[pulse_0.7s_ease-in-out_infinite_0.2s]" />
                  <span className="w-0.5 h-2.5 bg-[#C99850] animate-[pulse_1.2s_ease-in-out_infinite_0.4s]" />
                </div>
                <span className="font-normal tracking-[0.18em] text-[#37332F]">Lyd til</span>
              </>
            )}
          </button>
        </div>
      </div>
    </section>
  );
}
